import { useCallback, useEffect } from 'react'
import { LEGACY_SELECTION_BEHAVIOR, recordLegacySelectionUsage } from '../../../../util/LegacySelectionUsage'

export default function useCompactQueueEventListeners({
  queueState,
  queueActions,
  contextMenu,
  setContextMenu,
  setShiftIsPressed,
  setLastCheckedIndex,
  filterRef,
}) {
  const checkedCount = Object.keys(queueState.selectedCheckboxes || {}).length
  const handleSpace = useCallback(
    (event, column) => {
      if (filterRef?.current?.contains(event.target)) {
        // If the event target is within the filterRef, do not propagate the space key event
        event.stopPropagation()
        return
      }
      const tableRows = document.querySelectorAll('#compactQueueTable tbody tr')
      const checkboxes = document.querySelectorAll('input[id*="messageCheckbox"]')
      const currentIndex = Number(Array.from(checkboxes).findIndex((checkbox) => checkbox.name === column))

      // Space only selects a row when a message checkbox has focus. Anywhere
      // else (e.g. a label reason textarea) currentIndex is -1; leave the key
      // alone so typing still works, instead of selecting `queueContents[-1]`
      // and crashing on `tableRows[-1].style` (DATA-12601).
      if (currentIndex < 0) return
      event.preventDefault()
      recordLegacySelectionUsage(LEGACY_SELECTION_BEHAVIOR.SPACE_SELECT_ADVANCE, {
        queueName: queueState.queueName,
        rowIndex: currentIndex,
      })

      queueActions.addCheckboxSelection(queueState.queueContents[currentIndex], true)
      setLastCheckedIndex(currentIndex)
      if (currentIndex + 1 < checkboxes.length) {
        checkboxes[currentIndex + 1].focus()
        if (tableRows[currentIndex + 1]) tableRows[currentIndex + 1].style.backgroundColor = 'lightgray'
        if (tableRows[currentIndex]) tableRows[currentIndex].style.backgroundColor = ''
      }
    },
    [filterRef, queueActions, queueState.queueContents, queueState.queueName, setLastCheckedIndex]
  )
  const handleTab = useCallback(
    (event, column) => {
      event.preventDefault()

      const tableRows = document.querySelectorAll('#compactQueueTable tbody tr')
      const checkboxes = document.querySelectorAll('input[id*="messageCheckbox"]')
      const currentIndex = Number(Array.from(checkboxes).findIndex((checkbox) => checkbox.name === column))

      // When no checkbox is focused yet (currentIndex === -1, e.g. the very first
      // Tab press), land on the first checkbox; otherwise advance to the next one.
      // Guarding against indexing one past the end of the collections avoids the
      // `TypeError: Cannot read properties of undefined` crash on the last row
      // (DATA-12259, DATA-12260), while still restoring the first-Tab focus that
      // the `currentIndex >= 0` guard had broken (DATA-12260 follow-up).
      const nextIndex = currentIndex < 0 ? 0 : currentIndex + 1
      if (nextIndex < checkboxes.length) {
        recordLegacySelectionUsage(LEGACY_SELECTION_BEHAVIOR.TAB_CHECKBOX_JUMP, {
          queueName: queueState.queueName,
          fromCheckbox: currentIndex >= 0,
          rowIndex: nextIndex,
        })
        checkboxes[nextIndex]?.focus()
        if (tableRows[nextIndex]) tableRows[nextIndex].style.backgroundColor = 'lightgray'
        if (currentIndex >= 0 && tableRows[currentIndex]) tableRows[currentIndex].style.backgroundColor = ''
      }
      setLastCheckedIndex(currentIndex)
    },
    [queueState.queueName, setLastCheckedIndex]
  )
  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault()

      setContextMenu({
        x: event.pageX,
        y: event.pageY - 150,
      })
    },
    [setContextMenu]
  )

  const closeContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [setContextMenu])

  useEffect(() => {
    const myElement = document.getElementById('compactQueueTable')
    const contextMenuHandler = (event) => {
      // Exclude links and their children from triggering the context menu
      if (event.target.closest('a')) return
      handleContextMenu(event)
    }
    myElement?.addEventListener('contextmenu', contextMenuHandler)
    return () => {
      myElement?.removeEventListener('contextmenu', contextMenuHandler)
    }
  }, [handleContextMenu])

  useEffect(() => {
    if (contextMenu && (checkedCount <= queueState.checkboxConfirmLimit || !queueState.confirmDialogOpen)) {
      document.addEventListener('click', closeContextMenu)
    } else {
      document.removeEventListener('click', closeContextMenu)
    }

    return () => {
      document.removeEventListener('click', closeContextMenu)
    }
  }, [
    checkedCount,
    closeContextMenu,
    contextMenu,
    queueState.checkboxConfirmLimit,
    queueState.confirmDialogOpen,
    queueState.selectedCheckboxes,
  ])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Shift') {
        setShiftIsPressed(true)
      }

      if (event.key === ' ') {
        handleSpace(event, event.target.name)
      }

      if (event.key === 'Tab') {
        handleTab(event, event.target.name)
      }
    }

    const handleKeyUp = (event) => {
      if (event.key === 'Shift') {
        setShiftIsPressed(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleSpace, handleTab, setShiftIsPressed])

  useEffect(() => {
    const tableRows = document.querySelectorAll('#compactQueueTable tbody tr')

    const handleMouseEnter = (event) => {
      event.currentTarget.style.backgroundColor = 'lightgray'
    }

    const handleMouseLeave = (event) => {
      event.currentTarget.style.backgroundColor = ''
    }

    tableRows.forEach((row) => {
      row.addEventListener('mouseenter', handleMouseEnter)
      row.addEventListener('mouseleave', handleMouseLeave)
    })

    return () => {
      tableRows.forEach((row) => {
        row.removeEventListener('mouseenter', handleMouseEnter)
        row.removeEventListener('mouseleave', handleMouseLeave)
      })
    }
  }, [queueState.queueContents])
}
